import { useEffect, useMemo } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom'
import { ThemeProvider, CssBaseline, useMediaQuery } from '@mui/material'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

import { lightTheme, darkTheme, withPrimaryColor } from './theme'
import { queryClient } from './lib/queryClient'
import { useAuthStore } from './store/authStore'
import { useThemeStore } from './store/themeStore'
import { useAppColorStore } from './store/appColorStore'
import { api } from './lib/api'
import { ApiError } from '@cmms/api-client'

import { LoginPage } from './features/auth/LoginPage'
import { ForgotPasswordPage } from './features/auth/ForgotPasswordPage'
import { ResetPasswordPage } from './features/auth/ResetPasswordPage'
import { RegisterOrgPage } from './features/auth/RegisterOrgPage'
import { AcceptInvitePage } from './features/auth/AcceptInvitePage'
import { ChatSocketProvider } from './hooks/useChatSocket'
// HSE fork: ChecklistsPage removed.
import { ChatNotificationHost } from './components/ChatNotificationHost'
import { ProtectedRoute } from './features/auth/ProtectedRoute'
import { AppShell } from './layouts/AppShell'
import { OverviewPage } from './features/overview/OverviewPage'
// UsersPage kept in features/users/ for backend-wired settings use, but
// the main /users route now redirects to /people.
// import { UsersPage } from './features/users/UsersPage'
// HSE fork: Work Orders, Tasks, Locations, Assets, Inventory, Vendors,
// Workflows, and Asset Categories are stripped from the UI — they were
// CMMS-specific surfaces that don't fit the HSE product. The backend
// modules still exist under HSE/api/src/modules/ for now; they can be
// removed once the HSE data model diverges.
import { RequestsPage } from './features/requests/RequestsPage'
import { PeoplePage } from './features/people/PeoplePage'
import { SitesPage } from './features/sites/SitesPage'
import { PublicReportPage } from './features/public-report/PublicReportPage'
import { SettingsPage } from './features/settings/SettingsPage'
import { ChatsPage } from './features/chats/ChatsPage'
// HSE fork: HelpPage removed.
import { ConfirmDialogProvider } from './components/ConfirmDialog'
import { SnackbarHost } from './components/SnackbarHost'

function HomeRedirect() {
  const accessToken = useAuthStore((s) => s.accessToken)
  return <Navigate to={accessToken ? '/overview' : '/login'} replace />
}

export default function App() {
  const themeMode = useThemeStore((s) => s.mode)
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)')
  const accessToken = useAuthStore((s) => s.accessToken)
  const isHydrated = useAuthStore((s) => s.isHydrated)
  const setUser = useAuthStore((s) => s.setUser)
  const clear = useAuthStore((s) => s.clear)

  const userColor = useAppColorStore((s) => s.primaryColor)

  const theme = useMemo(() => {
    const useDark = themeMode === 'dark' || (themeMode === 'system' && prefersDark)
    const base = useDark ? darkTheme : lightTheme
    return userColor ? withPrimaryColor(base, userColor) : base
  }, [themeMode, prefersDark, userColor])

  const setAppColor = useAppColorStore((s) => s.setPrimaryColor)

  useEffect(() => {
    if (!isHydrated || !accessToken) return
    api.auth
      .me()
      .then((user) => {
        setUser(user)
        // Sync user's saved color preference on login
        api.users.getProfile().then((profile) => {
          const savedColor = (profile as any)?.preferences?.primaryColor
          if (savedColor && typeof savedColor === 'string') {
            setAppColor(savedColor)
          }
        }).catch(() => {})
      })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) clear()
      })
  }, [isHydrated, accessToken, setUser, clear, setAppColor])

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SnackbarHost />
        <ConfirmDialogProvider>
        <ChatSocketProvider>
        <BrowserRouter>
          <ChatNotificationHost />
          <Routes>
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register-org" element={<RegisterOrgPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/invitations/accept" element={<AcceptInvitePage />} />

            {/* HSE fork: public site-QR intake — reachable without a
                login. QR stickers encode `/report/<orgSlug>/<siteId>`,
                so the URL is guessable only if you have that pair. */}
            <Route path="/report/:orgSlug/:siteId" element={<PublicReportPage />} />
            {/* Path-based workspace slug — shareable login URL like
                /bacsys-demo/login so customers don't type the slug. Must
                come AFTER the other static paths so they win the match. */}
            <Route path="/:slug/login" element={<LoginPage />} />

            <Route element={<ProtectedRoute />}>
              <Route element={<AppShell />}>
                <Route path="/overview" element={<OverviewPage />} />
                <Route path="/overview/detailed" element={<OverviewPage />} />
                <Route path="/overview/my" element={<OverviewPage />} />

                {/* Real, wired-up pages */}
                <Route path="/people"           element={<PeoplePage />} />
                <Route path="/people/:userId"  element={<PeoplePage />} />
                {/* HSE fork: Sites promoted to a top-level nav item. The
                    /settings/sites URLs redirect to the new location so
                    old links / bookmarks don't 404. */}
                <Route path="/sites"           element={<SitesPage />} />
                <Route path="/sites/:siteId"   element={<SitesPage />} />
                <Route path="/settings/sites"           element={<Navigate to="/sites" replace />} />
                <Route path="/settings/sites/:siteId"   element={<LegacySiteRedirect />} />
                <Route path="/settings"         element={<SettingsPage />} />

                {/* HSE fork: /work-orders, /tasks, /locations, /assets,
                    /categories, /vendors, /inventory, /workflows removed.
                    Anything that used to deep-link into them now
                    falls through to the catch-all redirect below. */}

                {/* HSE fork: Requests rebranded as Reports. Both URL
                    families render the same page so old bookmarks
                    still resolve. */}
                <Route path="/reports" element={<RequestsPage />} />
                <Route path="/reports/:requestId" element={<RequestsPage />} />
                <Route path="/requests" element={<Navigate to="/reports" replace />} />
                <Route path="/requests/:requestId" element={<LegacyRequestRedirect />} />
                {/* HSE fork: Checklists and Help & Support routes
                    removed — those modules aren't part of the HSE
                    product. Anything deep-linking into them falls
                    through to the catch-all redirect below. */}
                <Route path="/chats" element={<ChatsPage />} />
                <Route path="/chats/:conversationId" element={<ChatsPage />} />
                <Route path="/subscription" element={<Navigate to="/settings?tab=subscription" replace />} />

                {/* Aliases / backward-compat redirects */}
                <Route path="/dashboard" element={<Navigate to="/overview" replace />} />
                <Route path="/users"     element={<Navigate to="/people" replace />} />
                <Route path="/teams"     element={<Navigate to="/people" replace />} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </ChatSocketProvider>
        </ConfirmDialogProvider>
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </ThemeProvider>
    </QueryClientProvider>
  )
}

// Preserves the site id when redirecting an old `/settings/sites/:siteId`
// bookmark to the new top-level `/sites/:siteId` route. Plain `<Navigate>`
// can't template a URL param, so we read it and hand-craft the target.
function LegacySiteRedirect() {
  const { siteId } = useParams<{ siteId?: string }>()
  return <Navigate to={siteId ? `/sites/${siteId}` : '/sites'} replace />
}

// Same pattern for the /requests/:requestId → /reports/:requestId
// bookmark redirect.
function LegacyRequestRedirect() {
  const { requestId } = useParams<{ requestId?: string }>()
  return <Navigate to={requestId ? `/reports/${requestId}` : '/reports'} replace />
}
